/**
 * FERP Normalizer Registry
 *
 * DRR-027: Central registry for domain-specific normalizers
 *
 * This registry allows type-safe normalization of any entity from any subgraph
 * into NormalizedEntity<T> while preserving the original type in domainData.
 */

import type { NormalizedEntity, SubgraphId } from '@/types/normalized-entity';

import {
  // Original 11 normalizers
  normalizeTask,
  normalizeWorkflow,
  normalizeActor,
  normalizeQueue,
  normalizeConstraint,
  normalizeNotification,
  normalizePolicy,
  normalizeSchedule,
  normalizeEvent,
  normalizeContract,
  normalizeDependency,
  // DRR-027: 14 new normalizers
  normalizeTemplate,
  normalizeRole,
  normalizeTeam,
  normalizeValidation,
  normalizeInstance,
  normalizeApproval,
  normalizeDocument,
  normalizeTrace,
  normalizeCheckpoint,
  normalizeTransaction,
  normalizeAudit,
  normalizeAnalytics,
  normalizeResource,
  normalizeIntegration,
  // Original 11 type guards
  isTask,
  isWorkflow,
  isActor,
  isQueue,
  isConstraint,
  isNotification,
  isPolicy,
  isSchedule,
  isEvent,
  isContract,
  isDependency,
  // DRR-027: 14 new type guards
  isTemplate,
  isRole,
  isTeam,
  isValidation,
  isInstance,
  isApproval,
  isDocument,
  isTrace,
  isCheckpoint,
  isTransaction,
  isAudit,
  isAnalytics,
  isResource,
  isIntegration,
} from './ferp-core';

import {
  normalizePatient,
  normalizeEncounter,
  normalizeClaim,
  isPatient,
  isEncounter,
  isClaim,
} from './fhir';

import {
  normalizePayrollRun,
  normalizeEmployee,
  isPayrollRun,
  isEmployee,
} from './payroll';

import {
  normalizeInvoice,
  normalizeOrder,
  isInvoice,
  isOrder,
} from './ofbiz';

import {
  normalizeAnnotation,
  isAnnotation,
} from './annotations';

// ============================================================================
// TYPE GUARD REGISTRY
// ============================================================================

/**
 * Built-in type guards and their normalizers for fallback detection.
 * This array pattern reduces cognitive complexity vs. multiple if statements.
 */
const BUILTIN_NORMALIZERS: Array<{
  guard: (obj: unknown) => boolean;
  normalizer: (entity: unknown) => NormalizedEntity<unknown>;
}> = [
  // FERP Core primitives - Original 11
  { guard: isTask, normalizer: normalizeTask as (e: unknown) => NormalizedEntity<unknown> },
  { guard: isWorkflow, normalizer: normalizeWorkflow as (e: unknown) => NormalizedEntity<unknown> },
  { guard: isActor, normalizer: normalizeActor as (e: unknown) => NormalizedEntity<unknown> },
  { guard: isQueue, normalizer: normalizeQueue as (e: unknown) => NormalizedEntity<unknown> },
  { guard: isConstraint, normalizer: normalizeConstraint as (e: unknown) => NormalizedEntity<unknown> },
  { guard: isNotification, normalizer: normalizeNotification as (e: unknown) => NormalizedEntity<unknown> },
  { guard: isPolicy, normalizer: normalizePolicy as (e: unknown) => NormalizedEntity<unknown> },
  { guard: isSchedule, normalizer: normalizeSchedule as (e: unknown) => NormalizedEntity<unknown> },
  { guard: isEvent, normalizer: normalizeEvent as (e: unknown) => NormalizedEntity<unknown> },
  { guard: isContract, normalizer: normalizeContract as (e: unknown) => NormalizedEntity<unknown> },
  { guard: isDependency, normalizer: normalizeDependency as (e: unknown) => NormalizedEntity<unknown> },
  // FERP Core primitives - DRR-027: 14 new normalizers (complete 25-primitive coverage)
  { guard: isTemplate, normalizer: normalizeTemplate as (e: unknown) => NormalizedEntity<unknown> },
  { guard: isRole, normalizer: normalizeRole as (e: unknown) => NormalizedEntity<unknown> },
  { guard: isTeam, normalizer: normalizeTeam as (e: unknown) => NormalizedEntity<unknown> },
  { guard: isValidation, normalizer: normalizeValidation as (e: unknown) => NormalizedEntity<unknown> },
  { guard: isInstance, normalizer: normalizeInstance as (e: unknown) => NormalizedEntity<unknown> },
  { guard: isApproval, normalizer: normalizeApproval as (e: unknown) => NormalizedEntity<unknown> },
  { guard: isDocument, normalizer: normalizeDocument as (e: unknown) => NormalizedEntity<unknown> },
  { guard: isTrace, normalizer: normalizeTrace as (e: unknown) => NormalizedEntity<unknown> },
  { guard: isCheckpoint, normalizer: normalizeCheckpoint as (e: unknown) => NormalizedEntity<unknown> },
  { guard: isTransaction, normalizer: normalizeTransaction as (e: unknown) => NormalizedEntity<unknown> },
  { guard: isAudit, normalizer: normalizeAudit as (e: unknown) => NormalizedEntity<unknown> },
  { guard: isAnalytics, normalizer: normalizeAnalytics as (e: unknown) => NormalizedEntity<unknown> },
  { guard: isResource, normalizer: normalizeResource as (e: unknown) => NormalizedEntity<unknown> },
  { guard: isIntegration, normalizer: normalizeIntegration as (e: unknown) => NormalizedEntity<unknown> },
  // FHIR (healthcare)
  { guard: isPatient, normalizer: normalizePatient as (e: unknown) => NormalizedEntity<unknown> },
  { guard: isEncounter, normalizer: normalizeEncounter as (e: unknown) => NormalizedEntity<unknown> },
  { guard: isClaim, normalizer: normalizeClaim as (e: unknown) => NormalizedEntity<unknown> },
  // Payroll
  { guard: isPayrollRun, normalizer: normalizePayrollRun as (e: unknown) => NormalizedEntity<unknown> },
  { guard: isEmployee, normalizer: normalizeEmployee as (e: unknown) => NormalizedEntity<unknown> },
  // OFBiz (ERP)
  { guard: isInvoice, normalizer: normalizeInvoice as (e: unknown) => NormalizedEntity<unknown> },
  { guard: isOrder, normalizer: normalizeOrder as (e: unknown) => NormalizedEntity<unknown> },
  // Annotations (DRR-062: cross-cutting, not a primitive)
  { guard: isAnnotation, normalizer: normalizeAnnotation as (e: unknown) => NormalizedEntity<unknown> },
];

// ============================================================================
// TYPES
// ============================================================================

/**
 * A normalizer function that converts a domain entity to NormalizedEntity
 */
export type NormalizerFn<T> = (entity: T) => NormalizedEntity<T>;

/**
 * Registry key format: subgraph:type (e.g., 'ferp-core:Task')
 */
export type RegistryKey = `${SubgraphId}:${string}`;

/**
 * Registry entry with type guard and normalizer
 */
interface RegistryEntry<T = unknown> {
  typeGuard: (obj: unknown) => obj is T;
  normalizer: NormalizerFn<T>;
}

// ============================================================================
// REGISTRY
// ============================================================================

/**
 * Internal registry mapping type names to normalizers
 */
const registry = new Map<RegistryKey, RegistryEntry>();

/**
 * Register a normalizer for a specific entity type
 *
 * @param subgraph - The source subgraph ID
 * @param typeName - The __typename of the entity
 * @param typeGuard - Type guard function to check if entity matches
 * @param normalizer - The normalizer function
 *
 * @example
 * ```typescript
 * registerNormalizer('fhir', 'Patient', isPatient, normalizePatient);
 * ```
 */
export function registerNormalizer<T>(
  subgraph: SubgraphId,
  typeName: string,
  typeGuard: (obj: unknown) => obj is T,
  normalizer: NormalizerFn<T>
): void {
  const key: RegistryKey = `${subgraph}:${typeName}`;
  registry.set(key, { typeGuard, normalizer } as RegistryEntry);
}

/**
 * Get a normalizer for a specific entity type
 *
 * @param subgraph - The source subgraph ID
 * @param typeName - The __typename of the entity
 * @returns The normalizer function or undefined if not found
 */
export function getNormalizer<T>(
  subgraph: SubgraphId,
  typeName: string
): NormalizerFn<T> | undefined {
  const key: RegistryKey = `${subgraph}:${typeName}`;
  const entry = registry.get(key);
  return entry?.normalizer as NormalizerFn<T> | undefined;
}

/**
 * Normalize any entity using the registry
 *
 * This function automatically detects the entity type using type guards
 * and applies the appropriate normalizer.
 *
 * @param entity - The entity to normalize (must have __typename)
 * @returns NormalizedEntity<T> or null if no normalizer found
 *
 * @example
 * ```typescript
 * const task = await fetchTask(id);
 * const normalized = normalize(task);
 * if (normalized) {
 *   console.log(normalized.safetyLevel);
 *   console.log(normalized.domainData.status); // Type-safe Task access
 * }
 * ```
 */
export function normalize<T extends { __typename?: string }>(
  entity: T | null | undefined
): NormalizedEntity<T> | null {
  // Handle null/undefined input
  if (entity === null || entity === undefined || typeof entity !== 'object') {
    return null;
  }

  // Try to find normalizer by __typename
  if (entity.__typename) {
    // Check all registry entries for this type
    for (const [key, entry] of registry.entries()) {
      const [, typeName] = key.split(':');
      if (typeName === entity.__typename && entry.typeGuard(entity)) {
        return entry.normalizer(entity) as NormalizedEntity<T>;
      }
    }
  }

  // Fallback: try built-in type guards
  for (const { guard, normalizer } of BUILTIN_NORMALIZERS) {
    if (guard(entity)) {
      return normalizer(entity) as NormalizedEntity<T>;
    }
  }

  // No normalizer found
  return null;
}

/**
 * Normalize multiple entities
 *
 * @param entities - Array of entities to normalize
 * @returns Array of NormalizedEntity<T>, excluding any that failed normalization
 */
export function normalizeMany<T extends { __typename?: string }>(
  entities: T[]
): NormalizedEntity<T>[] {
  const results: NormalizedEntity<T>[] = [];

  for (const entity of entities) {
    const normalized = normalize(entity);
    if (normalized) {
      results.push(normalized);
    }
  }

  return results;
}

/**
 * Check if an entity can be normalized
 *
 * @param entity - The entity to check
 * @returns true if a normalizer exists for this entity type
 */
export function canNormalize(entity: unknown): boolean {
  if (typeof entity !== 'object' || entity === null) {
    return false;
  }

  // Check built-in type guards
  for (const { guard } of BUILTIN_NORMALIZERS) {
    if (guard(entity)) {
      return true;
    }
  }

  // Check registry by __typename
  const typename = (entity as { __typename?: string }).__typename;
  if (typename) {
    for (const [key] of registry.entries()) {
      const [, typeName] = key.split(':');
      if (typeName === typename) {
        return true;
      }
    }
  }

  return false;
}

// ============================================================================
// BOOTSTRAP: Built-in normalizers are registered via BUILTIN_NORMALIZERS array
// ============================================================================

// All built-in normalizers (FERP Core, FHIR, Payroll, OFBiz) are registered
// via the BUILTIN_NORMALIZERS array for compile-time type safety.
//
// For custom subgraph types, use registerNormalizer() at runtime:
// registerNormalizer('custom-subgraph', 'CustomType', isCustomType, normalizeCustomType);
